import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useUserPermissions } from "../../../../UserPermissionsContext";
import { Select } from "antd";
import { useNavigate } from "react-router";

interface EditTicketProps {
  teacherId: number;
  onSuccess: () => void;
}

interface OptionType {
  id: number;
  name: string;
}

interface OptionType1 {
  id: number;
  name: string;
  has_call_back_time: boolean;
}

const EditSedTicket = ({ teacherId, onSuccess }: EditTicketProps) => {
  const { Option } = Select;
  const navigate = useNavigate();
  const { webApi } = useUserPermissions();
  const jwtToken = Cookies.get("authToken");

  const [categories, setCategories] = useState<OptionType[]>([]);
  const [subCategories, setSubCategories] = useState<OptionType1[]>([]);
  const [showCallback, setShowCallback] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<any>({});

  const [formData, setFormData] = useState({
    category: "",
    sub_category: "",
    call_back_time: "",
    description: "",
  });

  const options = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  // Fetch Categories
  const fetchCategories = async () => {
    try {
      const res = await fetch(
        `${webApi}sed/sed_ticket_category_dropdown_for_ticket_operations/`,
        options,
      );
      if (!res.ok) {
      const errorData = await res.json();
      if (errorData.code === "token_not_valid") {
        navigate("/login");
      }
      throw new Error(errorData.detail || "Failed to fetch user data");
    }
      const data = await res.json();
      setCategories(data || []);
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch Sub Categories
  const fetchSubCategories = async (categoryId: string) => {
    try {
      const res = await fetch(
        `${webApi}sed/sed_ticket_subcategory_dropdown_for_ticket_operations/?category=${categoryId}`,
        options,
      );
      const data = await res.json();
      setSubCategories(data || []);
      return data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  // Fetch Existing Ticket Data
  const fetchTicketDetails = async () => {
    try {
      const res = await fetch(
        `${webApi}sed/sed_teacher_ticket_operations/${teacherId}/`,
        options,
      );
      const data = await res.json();

      const subCategoryData = await fetchSubCategories(String(data.category));

      const selectedSubCategory = subCategoryData.find(
        (item: OptionType1) => item.id === data.sub_category,
      );

      const callbackRequired = selectedSubCategory?.has_call_back_time ?? false;

      setShowCallback(callbackRequired);

      setFormData({
        category: String(data.category || ""),
        sub_category: String(data.sub_category || ""),
        call_back_time: data.call_back_time || "",
        description: data.description || "",
      });
    } catch (error) {
      console.error(error);
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchTicketDetails();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCategoryChange = async (value: string) => {
    setFormData((prev) => ({
      ...prev,
      category: value,
      sub_category: "",
      call_back_time: "",
    }));

    setShowCallback(false);
    await fetchSubCategories(value);
  };

  const handleSubCategoryChange = (value: string) => {
    const selectedSubCategory = subCategories.find(
      (item) => item.id === Number(value),
    );

    const callbackRequired = selectedSubCategory?.has_call_back_time ?? false;

    setShowCallback(callbackRequired);

    setFormData((prev) => ({
      ...prev,
      sub_category: value,
      call_back_time: formatDateTimeLocal(formData.call_back_time),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      teacher: teacherId,
      category: Number(formData.category),
      sub_category: Number(formData.sub_category),
      call_back_time: showCallback ? formData.call_back_time : null,
      description: formData.description,
    };

    try {
      setLoading(true);

      const res = await fetch(
        `${webApi}sed/sed_teacher_ticket_operations/${teacherId}/`,
        {
          method: "PUT", // or PATCH
          headers: options.headers,
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();
      if (!res.ok) {
        setErrorMessage(data);
        return;
      }
      onSuccess();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTimeLocal = (dateString: string) => {
    if (!dateString || dateString === "None") return "";

    const date = new Date(dateString);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  if (pageLoading) {
    return <div>Loading ticket...</div>;
  }

  return (
    <div className="card border-0">
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Category */}
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Category</label>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select Category"
                  value={formData.category || undefined}
                  onChange={handleCategoryChange}
                  showSearch
                  optionFilterProp="children"
                >
                  {categories.map((item) => (
                    <Option key={item.id} value={String(item.id)}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
                {errorMessage.category && (
                  <div className="text-danger">
                    {errorMessage.category[0]}
                  </div>
                )}
              </div>
            </div>

            {/* Sub Category */}
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Sub Category</label>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select Sub Category"
                  value={formData.sub_category || undefined}
                  onChange={handleSubCategoryChange}
                  disabled={!formData.category}
                  showSearch
                  optionFilterProp="children"
                >
                  {subCategories.map((item) => (
                    <Option key={item.id} value={String(item.id)}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </div>
              {errorMessage.sub_category && (
                <div className="text-danger">
                  {errorMessage.sub_category[0]}
                </div>
              )}
            </div>

            {/* Callback */}
            {showCallback && (
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Call Back Time</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    name="call_back_time"
                    value={formatDateTimeLocal(formData.call_back_time)}
                    onChange={handleInputChange}
                  />
                  {errorMessage.call_back_time && (
                    <div className="text-danger">
                      {errorMessage.call_back_time[0]}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  rows={4}
                  className="form-control"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            Save
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditSedTicket;
